import axios from "axios";
import {
  baseurl as API_BASE_URL,
  LocalStorageItems
} from "../../helpers/constants";
import storageService from "../storageService/storageService";
import {
  AxiosHeaders,
  BoundUser,
  BuffetCategories,
  BuffetEpgItems,
  CategoryList,
  ChannelEpg,
  ChannelId,
  ChannelList,
  ChannelsToLock,
  CheckPinDto,
  ChildUpdate,
  LanguageList,
  LockedChannelSuccess,
  LoginCredentialsDto,
  Movie,
  Movies,
  MyChannel,
  NewUser,
  Promotions,
  ProviderDataList,
  TvShow,
  TvShows,
  UpdatePinDto,
  UserChannelConfig,
  UserChildren,
  UserInfo,
  UserUpdate,
  VoDGenres
} from "./api.types";
import dayjs from "dayjs";

export const http = axios.create({
  baseURL: API_BASE_URL,
  responseType: "json"
});

http.interceptors.request.use((config) => {
  const headers: AxiosHeaders = {
    Accept: "application/json",
    "Content-Type": "application/json"
  };

  if (config.headers) {
    config.headers = Object.assign(headers, config.headers);
  }

  if (storageService.hasItem(LocalStorageItems.Token)) {
    headers.Authorization = `Bearer ${storageService.getItem(
      LocalStorageItems.Token
    )}`;
  }

  config.headers = headers;
  return config;
});
//user controller
const logIn = (logInCredentials: LoginCredentialsDto): Promise<string> =>
  http.post<string>("user/login", logInCredentials).then((res) => res.data);

const logOut = (): Promise<void> =>
  http.post<void>("user/logout").then((res) => res.data);

const getUserInfo = (): Promise<UserInfo> =>
  http.get<UserInfo>("user/me").then((res) => res.data);

const getUserChildrenInfo = (): Promise<UserChildren> =>
  http.get<UserChildren>("user/children").then((res) => res.data);

const getBoundUsers = (): Promise<BoundUser[]> =>
  http.get<BoundUser[]>("user/my-users").then((res) => res.data);

const verifyPin = (pin: CheckPinDto): Promise<void> =>
  http.post<void>("user/check-pin-code", pin).then((res) => res.data);

const updatePin = (pinArr: UpdatePinDto): Promise<void> =>
  http.put<void>("user/update-pin-code", pinArr).then((res) => res.data);

const updateCurrentUser = (
  userData: UserUpdate,
  encodedPin: string
): Promise<void> =>
  http
    .put<void>("user/update", userData, {
      headers: {
        "ott-pin-code": encodedPin
      }
    })
    .then((res) => res.data);

const updateChildUser = (
  userData: ChildUpdate,
  encodedPin: string
): Promise<void> =>
  http
    .put<void>("user/update-child/" + userData.id, userData, {
      headers: {
        "ott-pin-code": encodedPin
      }
    })
    .then((res) => res.data);

const createChildUser = (
  userData: NewUser,
  encodedPin: string
): Promise<void> =>
  http
    .post<void>("user/create", userData, {
      headers: {
        "ott-pin-code": encodedPin
      }
    })
    .then((res) => res.data);

const deleteChildUser = (childId: number, encodedPin: string): Promise<void> =>
  http
    .delete<void>("user/" + childId, {
      headers: {
        "ott-pin-code": encodedPin
      }
    })
    .then((res) => res.data);

const changeUserLanguage = (langId: number): Promise<void> =>
  http.put<void>("user/set-lang/" + langId).then((res) => res.data);

const isLoggedIn = (token: string): Promise<boolean> =>
  http.get<boolean>("user/is-logged-in/" + token).then((res) => res.data);

//channel controller
const getMyChannels = (): Promise<ChannelList> =>
  http.get<ChannelList>("channel/my-channels").then((res) => res.data);

const getChannel = (channelId: number): Promise<MyChannel> =>
  http.get<MyChannel>("channel/" + channelId).then((res) => res.data);

const getUserData = (): Promise<UserChannelConfig[]> =>
  http.get<UserChannelConfig[]>("channel/user-data").then((res) => res.data);

const getChannelEpg = (
  channelId: number,
  date: string,
  offset: number
): Promise<ChannelEpg> =>
  http
    .get<ChannelEpg>(
      "channel/epg/" + channelId + "?date=" + date + "&offset=" + offset
    )
    .then((res) => res.data);

const setChannelAsFavourite = (channelId: number): Promise<void> =>
  http.post<void>("channel/favorite/" + channelId).then((res) => res.data);

const removeChannelAsFavourite = (channelId: number): Promise<void> =>
  http.post<void>("channel/unfavorite/" + channelId).then((res) => res.data);

const setLockedChannelList = (
  userId: number,
  lockedChannels: ChannelsToLock,
  encodedPin: string
): Promise<LockedChannelSuccess> =>
  http
    .post<LockedChannelSuccess>("channel/lock/" + userId, lockedChannels, {
      headers: {
        "ott-pin-code": encodedPin
      }
    })
    .then((res) => res.data);

const channelReorder = (channelOrder: ChannelId[]): Promise<void> =>
  http.post("channel/reorder", channelOrder).then((res) => res.data);

//categories controller
const getCategories = (): Promise<CategoryList> =>
  http.get<CategoryList>("categories").then((res) => res.data);

//promotions controller
const getPromotions = (): Promise<Promotions> =>
  http.get<Promotions>("promotions").then((res) => res.data);

//buffet controller
const getBuffetEpgItems = (): Promise<BuffetEpgItems> =>
  http.get<BuffetEpgItems>("buffet").then((res) => res.data);

const getBuffetCategories = (): Promise<BuffetCategories> =>
  http.get<BuffetCategories>("buffet/categories").then((res) => res.data);

const getBuffetEpgByCategory = (
  categoryCode: string,
  date?: string,
  offset?: number
): Promise<BuffetEpgItems> =>
  http
    .get<BuffetEpgItems>("buffet/" + categoryCode, {
      params: {
        date: date ? date : dayjs().format("YYYY-MM-DD"),
        offset: offset
      }
    })
    .then((res) => res.data);

//provider
const getProvider = (): Promise<ProviderDataList> =>
  http.get<ProviderDataList>("provider").then((res) => res.data);

const getLanguages = (): Promise<LanguageList> =>
  http.get<LanguageList>("language").then((res) => res.data);

//VoD
const getMoviesByGenre = (): Promise<VoDGenres> =>
  http.get<VoDGenres>("genre/movies").then((res) => res.data);

const getTvShowsByGenre = (): Promise<VoDGenres> =>
  http.get<VoDGenres>("genre/tv-shows").then((res) => res.data);

const getFavoriteMovies = (): Promise<Movies> =>
  http.get<Movies>("movie/favorites").then((res) => res.data);

const getFavoriteTvShows = (): Promise<TvShows> =>
  http.get<TvShows>("tv-show/favorites").then((res) => res.data);

const getMovie = (movieId: number): Promise<Movie> =>
  http.get<Movie>("movie/" + movieId).then((res) => res.data);

const getTvShow = (tvShowId: number): Promise<TvShow> =>
  http.get<TvShow>("tv-show/" + tvShowId).then((res) => res.data);

const setMovieAsFavorite = (movieId: number): Promise<void> =>
  http.post<void>("movie/favorite/" + movieId).then((res) => res.data);

const setTvShowAsFavorite = (tvShowId: number): Promise<void> =>
  http.post<void>("tv-show/favorite/" + tvShowId).then((res) => res.data);

const removeMovieFromFavorites = (movieId: number): Promise<void> =>
  http.post<void>("movie/unfavorite/" + movieId).then((res) => res.data);

const removeTvShowFromFavorites = (tvShowId: number): Promise<void> =>
  http.post<void>("tv-show/unfavorite/" + tvShowId).then((res) => res.data);

const getGenreMovies = (movieId: number): Promise<Movies> =>
  http.get<Movies>("genre/movies/" + movieId).then((res) => res.data);

export default {
  //user controller
  logIn,
  logOut,
  getUserInfo,
  getUserChildrenInfo,
  getBoundUsers,
  verifyPin,
  updatePin,
  updateCurrentUser,
  updateChildUser,
  createChildUser,
  deleteChildUser,
  changeUserLanguage,

  //channel controller
  getMyChannels,
  getChannel,
  getUserData,
  getChannelEpg,
  setChannelAsFavourite,
  removeChannelAsFavourite,
  setLockedChannelList,
  channelReorder,
  isLoggedIn,

  //Buffet controller
  getBuffetCategories,
  getBuffetEpgItems,
  getBuffetEpgByCategory,

  //categories controller
  getCategories,

  //promotions controller
  getPromotions,

  //provider
  getProvider,

  //language
  getLanguages,

  //VoD
  getMoviesByGenre,
  getTvShowsByGenre,
  getFavoriteMovies,
  getFavoriteTvShows,
  getMovie,
  getTvShow,
  setMovieAsFavorite,
  setTvShowAsFavorite,
  removeMovieFromFavorites,
  removeTvShowFromFavorites,
  getGenreMovies
};
